import React, { useState, useRef } from 'react'
import { styles } from './index.sass'
import { Button, message, Card, Input, Spin as AntdSpin, Modal } from 'antd'
import axios from '../../api/axios_ml'
import { cleanHtmlContent } from '../../utils/htmlCleaner'
import RichTextEditor from '../../components/richTextEditor'

export const ApeironBlog = () => {
  const [topic, setTopic] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const editorContentRef = useRef('')

  const handleGenerate = async (regenerate = false) => {
    if (!topic.trim()) {
      message.error('Please enter a topic for blog generation')
      return
    }

    setLoading(true)
    try {
      const payload = regenerate ? { topic, regenerate: true } : { topic }
      const response = await axios.post(
        `${process.env.REACT_APP_ML_API_ENDPOINT}/api/ml/generate-blog`,
        payload,
      )
      const cleanedHtml = cleanHtmlContent(response.content_html)

      setContentHtml(cleanedHtml)
      message.success('Blog generated successfully!')
    } catch (err) {
      console.error('Error generating blog:', err)
      message.error(
        err.message || 'Failed to generate blog. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: 'Regenerate Blog',
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  return (
    <div className={styles}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="loading-text">Generating content</div>
            <div className="loading-subtext">
              This will take less than 3 minutes. Please wait...
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>Blog Content Generator</h2>
        <div className="input-section">
          <Input
            placeholder="Enter topic for blog generation"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <div className="actions">
            <Button type="primary" onClick={handleGenerate} loading={loading}>
              Generate Blog
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={!contentHtml || loading}
            >
              Regenerate
            </Button>
            <Button onClick={handleCopy} disabled={!contentHtml}>
              Copy Content
            </Button>
            <Button onClick={handleEdit} disabled={!contentHtml}>
              Edit Content
            </Button>
          </div>
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                  <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </div>
                <RichTextEditor
                  value={editedContent}
                  onChange={handleEditorChange}
                  placeholder="Edit your blog content here..."
                />
              </div>
            ) : (
              <div
                className="content-preview"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
